import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Flex, Group, Popover, Text } from "@mantine/core";
import { itemBorder, layerGroupRadius, layerItemHeight } from "../../utils/style.js";
import { LayerGroupIcon } from "./LayerGroupIcon.js";
import { memo, useCallback, useMemo } from "react";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { Event } from "../../constants/events.js";
import { FormattedMessage } from "react-intl";
import { useAppAction, useAppSelector } from "../../hooks/redux.js";
import { changeLayerGroup, getLayerGroupValues } from "../../reducers/layersReducer.js";
import { DeleteLayerGroupModal } from "./DeleteLayerGroupModal.js";
export const LayerItemName = ({ name }) => {
    return (_jsx(Text, { fz: 14, fw: 600, truncate: true, children: name }));
};
export const LayerListGroup = memo(({ rightControl, icon, parent, id, label, expanded, childGroups, indent, onClick }) => {
    const captureEvent = useCaptureEvent();
    const groups = useAppSelector(getLayerGroupValues);
    const changeGroup = useAppAction(changeLayerGroup);
    const [openedPopover, { close: closePopover, open: openPopover }] = useDisclosure(false);
    const ref = useClickOutside(closePopover);
    const [openedDeleteGroupModal, { close: closeDeleteGroup, open: openDeleteGroup }] = useDisclosure(false);
    const handleOpen = useCallback((event) => {
        event.preventDefault();
        captureEvent(Event.OpenLayerItemRightClickMenu);
        openPopover();
    }, [captureEvent, openPopover]);
    const handleDelete = useCallback((event) => {
        event.preventDefault();
        captureEvent(Event.OpenDeleteLayerGroupModal);
        openDeleteGroup();
    }, [captureEvent, openDeleteGroup]);
    const handleChangeGroup = useCallback((from, to) => {
        captureEvent(Event.ChangeLayerGroup);
        changeGroup({ from, to, id });
        closePopover();
    }, [captureEvent, closePopover, changeGroup, id]);
    const groupOptions = useMemo(() => {
        const options = [];
        for (const group of groups) {
            if (group.value === parent) {
                continue;
            }
            if (group.value === id) {
                continue;
            }
            if (childGroups.some(child => child.value === group.value)) {
                continue;
            }
            options.push(group);
        }
        return options;
    }, [groups, parent, id, childGroups]);
    const borderRadius = expanded
        ? {
            borderTopLeftRadius: layerGroupRadius,
            borderTopRightRadius: layerGroupRadius,
        }
        : {
            borderRadius: layerGroupRadius,
        };
    return (_jsxs(Popover, { width: 200, position: "bottom", withArrow: true, closeOnClickOutside: true, shadow: "md", opened: openedPopover, children: [_jsx(Popover.Target, { children: _jsxs(Group, { h: layerItemHeight, className: "layer-group", pl: indent, gap: 8, wrap: "nowrap", onClick: onClick, onContextMenu: handleOpen, style: { ...borderRadius, border: itemBorder }, children: [_jsx(LayerGroupIcon, { icon: icon }), _jsx(LayerItemName, { name: label }), _jsx(Flex, { ml: "auto", pr: "xs", align: "center", children: rightControl })] }) }), _jsxs(Popover.Dropdown, { ref: ref, children: [groupOptions.length > 0 && (_jsxs(Popover, { width: 200, position: "right", withArrow: true, shadow: "md", children: [_jsx(Popover.Target, { children: _jsx(Button, { w: "100%", variant: "default", size: "xs", children: _jsx(FormattedMessage, { id: "button.changeParentGroup", defaultMessage: "Change group" }) }) }), _jsx(Popover.Dropdown, { children: groupOptions.map(({ value, label: groupLabel }) => (_jsx(Button, { w: "100%", variant: "default", size: "xs", onClick: () => {
                                        handleChangeGroup(parent, value);
                                    }, children: groupLabel }, value))) })] })), _jsx(Button, { w: "100%", variant: "default", size: "xs", onClick: handleDelete, children: _jsx(FormattedMessage, { id: "button.delete", defaultMessage: "Delete" }) })] }), openedDeleteGroupModal && _jsx(DeleteLayerGroupModal, { opened: true, onClose: closeDeleteGroup })] }));
});
LayerListGroup.displayName = "LayerListGroup";
